import { FIVE_G_PLUS_SPEED_THRESHOLD_MBPS } from '../../../common/constants/subscriptionConstants.js';
import {
  euMsg,
  finlandNordicsAndBalticsMsg2,
  mBpsMsg,
  outsideEuMsg,
  pcsMsg,
  speedMsg,
  t,
  textMessagesMsg,
  unlimitedMsg,
  uploadSpeedMsg,
  voiceMsg,
} from '../../../common/i18n/index.js';
import { eurosToCents, formatSumToString } from '../../../common/utils/priceUtils.js';
import {
  getCharacteristicValueWithAddonPriority,
  getDownloadSpeedInMbps,
  getLocalizedString,
  getSpecValueWithAddonPriority,
} from '../../../common/utils/productOfferingsUtils.js';
import { PriceType } from '../../HorizontalSubscriptionCard/types.js';
import type { ProductOfferingCharacteristic } from '../../../generated/api/productOfferingCharacteristic.js';
import type { ProductOfferingsResponse } from '../../../generated/api/productOfferingsResponse.js';

interface DefaultCardContentData {
  badges: Array<{ key: string; color: '5g'; text: string }>;
  infoItems: Array<{ key: string; icon: string; text: string }>;
  additionalFeatures: ProductOfferingCharacteristic[];
}

const connectionCoverageId = 'connection_coverage';
const euDataLimitId = 'eu_data_limit';

const getPrioritizedCharacteristics = (productOffering: ProductOfferingsResponse) => {
  const planCharacteristics = productOffering.planProductOffering?.productOfferingCharacteristic;
  return planCharacteristics && planCharacteristics.length > 0
    ? planCharacteristics
    : (productOffering.productOfferingCharacteristic ?? []);
};

/** Extracts tier pricing items from product offering for display as infoItems or tier list */
export const getTierPriceInfoItems = (
  productOffering: ProductOfferingsResponse
): Array<{ key: string; icon: string; text: string }> => {
  const bundleAddonGroup = productOffering.bundleAddonGroup ?? [];
  const tierOfferings = bundleAddonGroup[0]?.productOffering ?? [];

  return tierOfferings
    .map(addon => {
      const tierLabel = getLocalizedString(addon.name);
      const recurringPrice = addon.productOfferingPrice?.find(p => p.priceType === PriceType.RECURRING_CHARGE);
      const priceInCents = recurringPrice?.priceWithoutTax ?? 0;
      const priceEuros = formatSumToString(eurosToCents(priceInCents), true);

      return {
        key: `tier-${addon.id}`,
        icon: 'disturbance',
        text: `${tierLabel}: ${priceEuros} €`,
      };
    })
    .filter(item => item.text);
};

export const getDefaultCardContentData = (productOffering: ProductOfferingsResponse): DefaultCardContentData => {
  const downloadSpeedMbps = getDownloadSpeedInMbps(productOffering);
  const connectionCoverageData =
    getPrioritizedCharacteristics(productOffering).find(c => c.name === connectionCoverageId)?.value?.[0]?.value ?? '';
  const euDataSpeed = getCharacteristicValueWithAddonPriority(productOffering, euDataLimitId);

  const is5GPlus = downloadSpeedMbps > FIVE_G_PLUS_SPEED_THRESHOLD_MBPS;
  const is5G = downloadSpeedMbps > 0;

  let badges: Array<{ key: string; color: '5g'; text: string }> = [];
  if (is5GPlus) {
    badges = [{ key: 'badge-5g-plus', color: '5g', text: '5G+' }];
  } else if (is5G) {
    badges = [{ key: 'badge-5g', color: '5g', text: '5G' }];
  }

  const infoItems = [
    ...(downloadSpeedMbps > 0
      ? [
          {
            key: 'download-speed',
            icon: 'disturbance',
            text: t.WXU0('Speed {} Mbps', downloadSpeedMbps.toString()),
          },
        ]
      : []),
    ...(connectionCoverageData
      ? [
          {
            key: 'connection-coverage',
            icon: 'wifi',
            text: t.SLOI(connectionCoverageData),
          },
        ]
      : []),
    ...(euDataSpeed
      ? [
          {
            key: 'eu-data-limit',
            icon: 'globe',
            text: t.IJ5P('EU data {} GB/month', euDataSpeed),
          },
        ]
      : []),
  ];

  const additionalFeatures = getPrioritizedCharacteristics(productOffering).filter(
    c => c.name !== connectionCoverageId && c.name !== euDataLimitId
  );

  return {
    badges,
    infoItems,
    additionalFeatures,
  };
};

export const getDefaultModalSpecifications = (productOffering: ProductOfferingsResponse) => {
  const voiceAllowance = getCharacteristicValueWithAddonPriority(productOffering, 'talk');
  const smsAllowanceValue = getCharacteristicValueWithAddonPriority(productOffering, 'text_messaging');
  const smsAllowance = smsAllowanceValue ? `${smsAllowanceValue} ${t.B3MG(pcsMsg)}` : '';
  const euDataSpeed = getCharacteristicValueWithAddonPriority(productOffering, euDataLimitId);
  const reissunettiAllowance = getCharacteristicValueWithAddonPriority(productOffering, 'reissunetti_allowance');
  const downloadSpeedMbps = getDownloadSpeedInMbps(productOffering);
  const uploadSpeed = getSpecValueWithAddonPriority(productOffering, 'upload_speed');

  return {
    voiceAndTextSpecs: [
      ...(downloadSpeedMbps
        ? [
            {
              name: t.XBSA(speedMsg),
              value: t.IJNS(mBpsMsg, downloadSpeedMbps.toString()),
            },
          ]
        : []),
      ...(uploadSpeed
        ? [
            {
              name: t.CQQ7(uploadSpeedMsg),
              value: t.IJNS(mBpsMsg, uploadSpeed),
            },
          ]
        : []),
      ...(voiceAllowance
        ? [
            {
              name: t.PV8D(voiceMsg),
              value: voiceAllowance,
            },
          ]
        : []),
      ...(smsAllowance
        ? [
            {
              name: t.MXR5(textMessagesMsg),
              value: smsAllowance,
            },
          ]
        : []),
    ],
    internetSpecs: [
      ...(reissunettiAllowance
        ? [
            {
              name: t.OMY8(finlandNordicsAndBalticsMsg2),
              value: t.MVTD(unlimitedMsg),
            },
          ]
        : []),
      ...(euDataSpeed
        ? [
            {
              name: t.ZFMI(euMsg),
              value: t.M09Z('{} GB', euDataSpeed),
            },
          ]
        : []),
      ...(reissunettiAllowance
        ? [
            {
              name: t.DTQ6(outsideEuMsg),
              value: reissunettiAllowance,
            },
          ]
        : []),
    ],
    has5gPlus: downloadSpeedMbps > FIVE_G_PLUS_SPEED_THRESHOLD_MBPS,
  };
};
